import React from "react";
import Navbar from "./Navbar";
import KeyFeatures from "./KeyFeatures";
import WhyUs from "./WhyUs";
import SchoolERP from "./Erp";
import AboutUs from "./AboutUs";
import OurMission from "./OurMission";
import Vision from "./Vision";
import ContactUs from "./ContactUs";
import Footer from "./Footer";
import DownloadApp from "./DownloadApp";
import { FaGooglePlay, FaApple } from "react-icons/fa";

const HomePage = () => {
  const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.posternova.posternova";
  const APP_STORE_URL = "https://apps.apple.com/in/app/editezy/id6753908761";

  return (
    <div className="bg-gray-950 min-h-screen">
      {/* Static Navbar */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <Navbar />
      </div>

      {/* Hero Section */}
      <section
        id="home"
        className="bg-gradient-to-br from-gray-950 via-gray-900 to-purple-950 flex flex-col lg:flex-row justify-around items-center px-4 sm:px-6 py-24"
      >
        {/* Left Side Text */}
        <div className="text-gray-300 w-full max-w-2xl mb-8 lg:mb-0 lg:pr-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
            Welcome to{" "}
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
              Edit Ezy
            </span>
          </h1>
          <p className="text-lg sm:text-xl lg:text-2xl mb-3 sm:mb-4 font-semibold text-purple-300">
            Create Stunning Festival & Business Posters in Minutes. No Design Skills Required.
          </p>
          <p className="text-gray-400 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
            Edit Ezy is India's smart poster-making platform that helps businesses, shop owners, creators, agencies, and professionals create beautiful, ready-made promotional posters instantly. Whether it's a festival greeting, business advertisement, birthday wish, anniversary greeting, or daily social media post, Edit Ezy has everything prepared for you.
          </p>
          <div className="mb-4 sm:mb-6 space-y-1.5 sm:space-y-2">
            <p className="text-gray-400 flex items-center gap-2 text-sm sm:text-base">
              <span className="text-purple-400">✓</span> No complicated editing
            </p>
            <p className="text-gray-400 flex items-center gap-2 text-sm sm:text-base">
              <span className="text-purple-400">✓</span> No graphic design experience
            </p>
            <p className="text-gray-400 flex items-center gap-2 text-sm sm:text-base">
              <span className="text-purple-400">✓</span> Just choose, personalize if needed, and share
            </p>
          </div>
          <p className="text-gray-300 mb-6 sm:mb-8 italic text-sm sm:text-base">
            Join thousands of businesses that save time and stay active on social media every single day.
          </p>

          <div className="bg-gradient-to-r from-purple-600/30 to-pink-600/30 backdrop-blur-sm rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8 inline-block border border-purple-500/30">
            <p className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">₹499/Year</p>
            <p className="text-gray-300 text-sm sm:text-base">Unlock premium templates, AI tools, and exclusive features.</p>
          </div>

          {/* Store Buttons */}
          <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 sm:px-6 py-3 sm:py-4 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-green-500/25 hover:-translate-y-0.5 transform group"
            >
              <FaGooglePlay size={22} className="sm:w-[26px] sm:h-[26px] text-white group-hover:scale-110 transition-transform duration-200 flex-shrink-0" />
              <div className="flex flex-col leading-tight">
                <span className="text-[10px] sm:text-xs text-green-100 uppercase tracking-widest">Get it on</span>
                <span className="text-sm sm:text-base font-semibold">Google Play</span>
              </div>
            </a>

            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 sm:px-6 py-3 sm:py-4 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-blue-500/25 hover:-translate-y-0.5 transform group"
            >
              <FaApple size={24} className="sm:w-[28px] sm:h-[28px] text-white group-hover:scale-110 transition-transform duration-200 flex-shrink-0" />
              <div className="flex flex-col leading-tight">
                <span className="text-[10px] sm:text-xs text-blue-100 uppercase tracking-widest">Download on the</span>
                <span className="text-sm sm:text-base font-semibold">App Store</span>
              </div>
            </a>
          </div>
        </div>

        {/* Right Side - App Screenshots */}
        <div className="w-full lg:w-auto flex justify-center items-center">
          <div className="relative">
            {/* Main Phone Frame */}
            <div className="relative mx-auto border-gray-800 dark:border-gray-800 bg-gray-800 border-[8px] sm:border-[14px] rounded-[2rem] sm:rounded-[2.5rem] h-[400px] sm:h-[500px] lg:h-[600px] w-[200px] sm:w-[250px] lg:w-[300px] shadow-2xl shadow-purple-500/20">
              {/* Notch */}

              {/* Dynamic Island */}
              <div className="h-[6px] sm:h-[8px] w-[40px] sm:w-[60px] bg-gray-950 absolute top-[6px] sm:top-[8px] left-1/2 -translate-x-1/2 rounded-full z-20"></div>

              {/* Screen Content */}
              <div className="relative w-full h-full overflow-hidden rounded-[1.5rem] sm:rounded-[1.8rem] bg-white">


                {/* App Screenshot */}
                <img
                  src="/mobile.png"
                  alt="Edit Ezy App Screenshot"
                  className="w-full h-full object-fill"
                />

                {/* Gradient Overlay for text readability */}
                <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-purple-900/80 to-transparent"></div>
              </div>
            </div>

            {/* Floating Elements for Visual Appeal */}
            <div className="absolute -top-4 -right-4 sm:-top-6 sm:-right-6 w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-xl animate-float">
              <span className="text-xl sm:text-2xl">🎨</span>
            </div>
            <div className="absolute -bottom-4 -left-4 sm:-bottom-6 sm:-left-6 w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center shadow-xl animate-float animation-delay-2000">
              <span className="text-lg sm:text-xl">✨</span>
            </div>
            <div className="absolute top-1/2 -right-6 sm:-right-8 w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-xl animate-float animation-delay-1000">
              <span className="text-sm sm:text-base">🚀</span>
            </div>
          </div>
        </div>
      </section>

      {/* Other Sections */}
      <div>
        <hr className="border-gray-800" />
        <section id="features"><KeyFeatures /></section>
        <hr className="border-gray-800" />
        <section id="whyus"><WhyUs /></section>
        <hr className="border-gray-800" />
        <section id="schoolerp"><SchoolERP /></section>
        <hr className="border-gray-800" />
        <section id="aboutus"><AboutUs /></section>
        <hr className="border-gray-800" />
        <section id="ourmission"><OurMission /></section>
        <hr className="border-gray-800" />
        <section id="vision"><Vision /></section>
        <hr className="border-gray-800" />
        <section id="download"><DownloadApp /></section>
        <hr className="border-gray-800" />
        <section id="contactus"><ContactUs /></section>
        <hr className="border-gray-800" />
        <Footer />
      </div>

      {/* Add custom CSS for floating animation */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animation-delay-1000 {
          animation-delay: 1s;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
      `}</style>
    </div>
  );
};

export default HomePage;